import { useState, useMemo } from 'react';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { FileInputValue, FileInput } from '../../../../../input';
import { useForm } from '../../hooks';

interface AddFileDialogProps {
  open: boolean;
  onClose: () => void;
  addFile: (file: FileInputValue) => void;
}

interface AddFileDialogForm {
  file: FileInputValue;
}

export const AddFileDialog = ({
  open,
  onClose,
  addFile,
}: AddFileDialogProps) => {
  const [file, setFile] = useState<FileInputValue>();
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      file,
    };
  }, [file]);

  const fieldValidators = useMemo(() => {
    return {
      file: [
        validator.required<AddFileDialogForm>(),
        validator.sizeLimit<AddFileDialogForm>(
          'Загрузите файл pdf, doc(x), xls(x), csv не более 10МБ',
          {
            field: `file`,
            sizeLimit: 10,
          },
        ),
        validator.requiredIfFile<AddFileDialogForm>(
          'Загрузите файл pdf, doc(x), xls(x), csv не более 10МБ',
          {
            field: 'file',
            fileTypes: [
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'text/csv',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
            ],
          },
        ),
      ],
    };
  }, [validator]);

  const { errors, touched, validateField, hasValidationErrors, showError } =
    useForm<AddFileDialogForm>(allValues, fieldValidators);

  const handleChangeFileInput = async (value?: FileInputValue | undefined) => {
    setFile(value);
    await validateField('file', value);
    showError('file');
  };

  const handleSubmit = () => {
    onClose();
    if (file) {
      addFile(file);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogContent
          title="Добавление файла"
          onClose={onClose}
          actions={
            <>
              <Button variant="tertiaryLight" size="large" onClick={onClose}>
                Отменить
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={hasValidationErrors}
              >
                Добавить
              </Button>
            </>
          }
        >
          <FileInput
            fullWidth
            required
            label="Файл"
            value={file}
            placeholder="Загрузите файл pdf, doc(x), xls(x), csv не более 10МБ"
            onChange={handleChangeFileInput}
            error={touched.file && !!errors.file}
            errorText={touched.file ? errors.file : undefined}
          />
        </DialogContent>
      </form>
    </Dialog>
  );
};
